import type { AxiosResponse } from 'axios';
import React, { createContext, useCallback, useContext } from 'react';
import {
	driversRegistryResults,
	extractOfDataQuery,
	filterErrorStatus,
	useAxiosInstance,
	usePoMutation,
	usePoQuery,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import { useProcessControl, useServiceAuthorization } from '@gov-nx/core/hooks';
import { getRequiredDataBoxes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { compose } from '@gov-nx/core/types';
import { useDataBoxAccess } from '@gov-nx/module/data-box';
import type { ServiceCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type { FormData, ServiceContextTypes } from './service.types';
import { getDriversLicenceInfo } from './utils';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

const AUTHORIZATION_SERVICE_NAME = 'BODOVE_HODNOCENI_RIDICE';

export function VypisBodovehoHodnoceniRidiceContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const databoxAccess = useDataBoxAccess({
		requiredTypes: requiredDataBoxes,
	});
	const { toastMessageSuccess } = useMessageEvents({});
	const { t } = useLocale(code);
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const formDefinition = FormInstance({
		dataBoxTypes: requiredDataBoxes,
		isLowAuthorization,
	});
	const { controls, setControls } = useProcessControl({ initialLoading: true });
	const source = 'REGISTR_RIDICU_V2';

	const driversRegistryQuery = usePoQuery(
		{
			queryKey: ['service', source],
			queryFn: () => driversRegistryResults(axiosInstance, source),
			refetchOnWindowFocus: false,
			onSuccess: () => {
				setControls({ initialLoading: false });
			},
			onError: (error) => {
				setControls({ initialError: error, initialLoading: false });
			},
		},
		{
			errorIgnoreFilter: filterErrorStatus(500, (initialError) =>
				setControls({ initialError })
			),
		}
	);

	const driversInfo = getDriversLicenceInfo(driversRegistryQuery.data);

	const submitMutation = usePoMutation<AxiosResponse<void>, FormData>({
		mutationFn: compose(
			extractOfDataQuery(axiosInstance),
			prepareSubmitData(driversInfo.licenceNumber)
		),
		onSuccess: (response) => {
			setControls({ processLoading: false });
			formDefinition.formReset();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('form.messages.odeslano'));
			};

			if (authContext) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					response.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
		onError: (error) => {
			setControls({ processError: error, processLoading: false });
			formDefinition.formReset();
		},
	});

	const handleSubmit = useCallback(async () => {
		setControls({ processError: null, processLoading: true });

		return submitMutation.mutate(formDefinition.formMethods.getValues());
	}, [formDefinition.formMethods, setControls, submitMutation]);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);

	const infoBox =
		driversInfo.points && driversInfo.points >= 5
			? t('form.messages.velky-pocet-bodu-varovani')
			: undefined;

	return (
		<ServiceContext.Provider
			value={{
				code,
				formDefinition,
				onSubmit,
				submitMutation,
				driversInfo,
				infoBox,
				maxDriversPoints: 12,
				controls,
				setControls,
				requiredDataBoxes,
				databoxAccess,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useVypisBodovehoHodnoceniRidiceContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
